(ns common.resource.query.social-settings
  (:require [cljs-bean.core :refer [->clj]]
            [clojure.set :as set]
            [common.queries :refer [invalidate-queries prevent-client-error-retries use-query]]
            [common.queries.http :as qhttp]
            [common.util :as util]
            [manage.adset.hooks :as ah]
            [re-frame.core :as rf]
            [uix.core :as uix :refer [defhook]]
            [uix.re-frame :refer [use-subscribe]]))

(defn upsert-social-settings-mutation [advertiser-id handle-error]
  #js {:mutationKey #js ["upsert-social-settings" advertiser-id]
       :mutationFn (fn [{:keys [meta-ad-account-id] :as params}]
                     (rf/dispatch [:loader/open])
                     (qhttp/put (qhttp/render :api/advertiser-social-settings-by-id
                                              {:advertiser-id advertiser-id
                                               :meta-ad-account-id meta-ad-account-id})
                                {:body (dissoc params :meta-ad-account-id)}))
       :onSettled #(rf/dispatch [:loader/close])
       :onSuccess (fn []
                    (invalidate-queries {:queryKey ["social-settings" advertiser-id]})
                    (invalidate-queries {:queryKey ["adset-meta-settings" advertiser-id]})
                    (rf/dispatch [:ocp/close])
                    (rf/dispatch [:response-handler/handle-response
                                  {:type :success
                                   :message "Ad account configuration saved"}]))
       :onError (fn [{:keys [data status]}]
                  (if (= status 400)
                    (handle-error (-> data ->clj :error))
                    (rf/dispatch [:response-handler/handle-response
                                  {:type :error
                                   :message "Failed to save Ad account configuration"}])))})

(defn delete-social-settings-mutation [advertiser-id handle-error]
  #js {:mutationKey #js ["delete-social-settings" advertiser-id]
       :mutationFn (fn [{:keys [meta-ad-account-id]}]
                     (rf/dispatch [:loader/open])
                     (qhttp/delete (qhttp/render :api/advertiser-social-settings-by-id
                                                 {:advertiser-id advertiser-id
                                                  :meta-ad-account-id meta-ad-account-id})))
       :onSettled #(rf/dispatch [:loader/close])
       :onSuccess (fn []
                    (invalidate-queries {:queryKey ["social-settings" advertiser-id]})
                    (rf/dispatch [:response-handler/handle-response
                                  {:type :success
                                   :message "Ad account configuration deleted"}]))
       :onError (fn [{:keys [data status]}]
                  (if (= status 400)
                    (handle-error (-> data ->clj :error))
                    (rf/dispatch [:response-handler/handle-response
                                  {:type :error
                                   :message "Failed to delete Ad account configuration"}])))})

(defn adset-meta-settings-query [advertiser-id adset-id ad-account-id]
  #js {:queryKey #js ["adset-meta-settings" advertiser-id adset-id ad-account-id]
       :queryFn (fn [_ctx]
                  (qhttp/get (qhttp/render :api/advertiser-adset-meta-settings
                                           {:advertiser-id advertiser-id
                                            :adset-id adset-id
                                            :ad-account-id ad-account-id})))
       :enabled (boolean (and advertiser-id adset-id ad-account-id))
       :staleTime (* 5 60 1000)})

(defn- meta-account-options [^js data]
  (map #(set/rename-keys % {:meta-ad-account-id :id})
       (:data (->clj data))))

(defn social-settings-query [advertiser-id]
  #js {:queryKey #js ["social-settings" advertiser-id]
       :queryFn (fn [_ctx]
                  (qhttp/get (qhttp/render :api/advertiser-social-settings
                                           {:advertiser-id advertiser-id})))
       :enabled (boolean advertiser-id)
       :staleTime (* 5 60 1000)
       :select meta-account-options}) ; 5 minutes

(defn- tiktok-account-options [^js data]
  (map #(set/rename-keys % {:name :description})
       (->clj data)))

(defn tiktok-accounts-query [advertiser-id]
  #js {:queryKey #js ["tiktok-accounts" advertiser-id]
       :queryFn (fn [_ctx]
                  (qhttp/get (qhttp/render :api/advertiser-tiktok-accounts {:advertiser-id advertiser-id})))
       :enabled (boolean advertiser-id)
       :staleTime (* 5 60 1000)
       :select tiktok-account-options})

(defn tiktok-identities-query [advertiser-id adset-id account-id]
  #js {:queryKey #js ["tiktok-identities" advertiser-id adset-id account-id]
       :queryFn (fn [_ctx]
                  (qhttp/get (qhttp/render :api/content-build-adset-tiktok-identities
                                           {:advertiser-id advertiser-id
                                            :adset-id adset-id}
                                           {:account-id account-id})))
       :enabled (boolean (and (seq account-id) adset-id))
       :staleTime (* 5 60 1000)})

(defn tiktok-campaigns-query [advertiser-id adset-id account-id]
  #js {:queryKey #js ["tiktok-campaigns" advertiser-id adset-id account-id]
       :queryFn (fn [_ctx]
                  (qhttp/get (qhttp/render :api/content-build-adset-tiktok-campaigns
                                           {:advertiser-id advertiser-id
                                            :adset-id adset-id}
                                           {:account-id account-id})))
       :enabled (boolean (and (seq account-id) adset-id))
       :staleTime (* 5 60 1000)})

(defn- meta-campaigns-options [^js data]
  (let [campaigns (-> data ->clj :campaigns :data)]
    (map #(set/rename-keys % {:name :description}) campaigns)))

(defn fetch-meta-campaigns-query [{:keys [advertiser-id adset-id meta-ad-account-id]}]
  #js {:queryKey #js ["meta-campaigns" advertiser-id adset-id meta-ad-account-id]
       :enabled (and (some? advertiser-id) (some? adset-id))
       :queryFn (fn [_ctx]
                  (qhttp/get (qhttp/render :api/content-build-adset-meta-campaigns
                                           {:advertiser-id advertiser-id
                                            :adset-id adset-id}
                                           {:ad-account-id meta-ad-account-id})))
       :staleTime (* 5 60 1000)
       :select meta-campaigns-options})

(defn fetch-meta-campaigns-by-account-query [{:keys [advertiser-id meta-ad-account-id]}]
  #js {:queryKey #js ["meta-campaigns" advertiser-id nil meta-ad-account-id]
       :enabled (boolean (and (some? advertiser-id) (seq meta-ad-account-id)))
       :queryFn (fn [_ctx]
                  (qhttp/get (qhttp/render :api/advertiser-meta-campaigns
                                           {:advertiser-id advertiser-id
                                            :ad-account-id meta-ad-account-id})))
       :staleTime (* 5 60 1000)
       :select meta-campaigns-options})

(defn ->integration-settings-type [channel]
  (if (contains? #{"meta" "tiktok"} channel) channel "dv"))

(defn fetch-advertiser-adset-integration-settings
  [{:keys [advertiser-id adset-id channel enabled]
    :or {enabled true}}]
  #js {:queryKey #js ["advertiser-adset-integration-settings" advertiser-id adset-id]
       :enabled (boolean (and enabled (some? adset-id)))
       :retry prevent-client-error-retries
       :queryFn (fn [_ctx]
                  (qhttp/get (qhttp/render
                              :api/advertiser-adset-integration-settings
                              {:advertiser-id advertiser-id
                               :adset-id adset-id
                               :integration-type (->integration-settings-type channel)})))
       :staleTime (* 60 1000)})

(def ^:private integration-settings-url-override-keys
  "If present on the `mutate` map, these override the closed-over `url-context`
  for the request URL; they are not sent in the JSON body."
  [:adset-id :channel])

(defn save-integration-settings-mutation
  "The `mutate` argument is the request body. Optional :adset-id and :channel
  on the same map override the hook's `url-context` for the URL; they are
  stripped from the body."
  [{:keys [advertiser-id adset-id] :as url-context}]
  #js {:mutationKey #js ["save-adset-integration-settings" advertiser-id adset-id]
       :mutationFn (fn [new-value]
                     (let [ctx (merge (select-keys url-context [:advertiser-id :adset-id :channel])
                                      (select-keys (or new-value {}) integration-settings-url-override-keys))
                           body (apply dissoc new-value integration-settings-url-override-keys)]
                       (qhttp/put (qhttp/render
                                   :api/advertiser-adset-integration-settings
                                   (assoc ctx :integration-type
                                          (->integration-settings-type (:channel ctx))))
                                  {:body (util/filter-nils-map body)})))
       :onSuccess
       (fn [_data variables]
         (let [ctx (merge (select-keys url-context [:advertiser-id :adset-id :channel])
                          (select-keys (or variables {}) integration-settings-url-override-keys))]
           (invalidate-queries
            {:queryKey ["advertiser-adset-integration-settings"
                        (:advertiser-id ctx)
                        (:adset-id ctx)]})))})

(defhook use-auto-publish []
  (let [{:keys [advertiser-id adset-id]} (use-subscribe [:url-context])
        channel (ah/use-channel)
        {:keys [data]}
        (use-query
         (fetch-advertiser-adset-integration-settings
          {:advertiser-id advertiser-id
           :adset-id adset-id
           :channel channel}))]
    (some-> data ->clj :auto-publish)))
